import { useState, useMemo, useCallback } from 'react'
import styled from '@emotion/styled'
import Typography from '@mui/material/Typography'
import { array, func, oneOfType, string, number } from 'prop-types'
import { Item } from './item'
import { SlideButton } from './slide-button'
import { useSliding } from './use-sliding'
import { t } from '@lingui/core/macro'

const direction = {
  BEFORE: 'before',
  AFTER: 'after',
  NONE: 'none',
}

const squareSize = 160

const SliderDiv = styled.div`
  display: flex;
  position: relative;
  width: 100%;
`

const SliderWrapper = styled.div`
  padding: 36px 0;
  overflow: hidden;
  position: relative;
`

const SliderContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ count }) => count}, ${squareSize}px);
  grid-template-rows: ${squareSize}px;
  grid-column-gap: 24px;
  padding: 0 56px;
  transition: transform 300ms ease 100ms;
  z-index: 3;
  max-width: 100%;
  transform: translate3d(${({ distance }) => distance}px, 0, 0);
  width: 75vw;
`

export const CvsCarousel = ({
  existingIntegrations,
  items = [],
  selectedItem = '',
  onItemSelect,
}) => {
  const [hovered, setHovered] = useState(-1)
  const { handlePrev, handleNext, containerRef, hasNext, hasPrev, distance } = useSliding(
    squareSize + 24,
    items?.length,
  )
  const getIndex = useCallback(i => items.map(({ id }) => id).indexOf(i), [items])

  const selectedIndex = useMemo(() => getIndex(selectedItem), [getIndex, selectedItem])
  const hoveredIndex = useMemo(() => getIndex(hovered), [getIndex, hovered])

  const mappedItems = useMemo(
    () =>
      items.map(item => {
        const thisIndex = getIndex(item.id)
        const index = selectedIndex !== -1 ? selectedIndex : hoveredIndex !== -1 ? hoveredIndex : -1
        return {
          ...item,
          direction:
            thisIndex === index
              ? direction.NONE
              : thisIndex > index
                ? direction.AFTER
                : direction.BEFORE,
        }
      }),
    [getIndex, items, selectedIndex, hoveredIndex],
  )

  const integrationsInboundDependant = []

  const integrationsLimitedToOneUsePerTemplate = [
    'bitly',
    'branch',
    'qr-code-monkey',
    'google-dcm-output',
    'facebook-ads-update',
    'adobe-experience-manager-inbound',
    'adobe-experience-manager-outbound',
  ]

  let disabledMessage = 'This integration can only be used once on a template'

  function shouldBeDisabled(item) {
    let disabled = false
    if (
      integrationsLimitedToOneUsePerTemplate.includes(item?.slug) &&
      existingIntegrations.includes(item?.slug)
    ) {
      disabled = true
      disabledMessage = 'This integration can only be used once on a template'
    }
    if (
      integrationsInboundDependant.find(
        i => i.outbound == item?.slug && !existingIntegrations.includes(i.inbound),
      )
    ) {
      disabled = true
      disabledMessage = 'First, add an integration of this type as an Inbound Integration.'
    }

    return disabled
  }

  return items.length > 0 ? (
    <SliderWrapper>
      <SliderDiv ref={containerRef}>
        {hasPrev && <SlideButton size={squareSize} onClick={handlePrev} type="prev" />}
        {hasNext && <SlideButton size={squareSize} onClick={handleNext} type="next" />}
        <SliderContainer distance={distance} count={items.length}>
          {mappedItems.map(item => (
            <Item
              onMouseEnter={() => {
                setHovered(item.id)
              }}
              onMouseLeave={() => {
                setHovered(null)
              }}
              isSelection={Boolean(selectedItem !== -1)}
              onClick={
                shouldBeDisabled(item)
                  ? () => null
                  : () => onItemSelect(prev => (prev === item.id ? -1 : item.id))
              }
              selected={selectedItem === item.id}
              hovered={hovered === item.id}
              key={item.id}
              item={item}
              disabled={shouldBeDisabled(item)}
              disabledMessage={disabledMessage}
            />
          ))}
        </SliderContainer>
      </SliderDiv>
    </SliderWrapper>
  ) : (
    <Typography>{t`No available integrations.`}</Typography>
  )
}

CvsCarousel.propTypes = {
  items: array,
  onItemSelect: func.isRequired,
  selectedItem: oneOfType([string, number]),
}
